import type { ClassLiveSession, ClassSummary, StudentAssignment } from '@pebblecode/shared';
import { useCallback, useEffect, useState } from 'react';

import { api } from '../api/client.js';
import { onLinkClick } from '../app/router.js';
import { Icon } from '../components/Icon.js';
import { timeAgo, useT } from '../i18n/index.js';
import { JoinClassForm } from '../pages/ClassesPages.js';
import { isTeacher, useMe } from '../state/auth.js';
import { useUiLang } from '../state/prefs.js';
import { HomeworkList } from './HomeworkList.js';

const LIVE_POLL_MS = 30_000;

/** "Live now": class sessions a teacher is broadcasting to my classes (ADR 0016). */
export function LiveNowCard() {
  const t = useT();
  const lang = useUiLang();
  const me = useMe();
  const [sessions, setSessions] = useState<ClassLiveSession[]>([]);
  const active = !!me?.active;

  useEffect(() => {
    if (!active) return;
    let live = true;
    const load = () =>
      api
        .classLive()
        .then((s) => live && setSessions(s))
        .catch(() => undefined);
    void load();
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') void load();
    }, LIVE_POLL_MS);
    return () => {
      live = false;
      clearInterval(timer);
    };
  }, [active]);

  if (sessions.length === 0) return null;
  return (
    <section className="live-card" aria-labelledby="live-now-title">
      <h2 id="live-now-title">
        <Icon name="radio" size={16} />
        {t.classLive.liveNow}
      </h2>
      {sessions.map((s) => (
        <div key={s.viewCode} className="live-row">
          <div>
            <strong>{s.title}</strong>
            <span>
              {t.classLive.liveWith(s.teacherName, s.className)} ·{' '}
              {t.live.startedAgo(timeAgo(s.startedAt, lang))}
            </span>
          </div>
          <a href={`/s/${s.viewCode}`} onClick={onLinkClick} className="btn btn-primary btn-sm">
            {t.classLive.join}
          </a>
        </div>
      ))}
    </section>
  );
}

/** A student's homework across their classes. */
export function HomeworkCard() {
  const t = useT();
  const me = useMe();
  const [items, setItems] = useState<StudentAssignment[] | null>(null);
  const active = !!me?.active;
  const load = useCallback(() => {
    api
      .homework()
      .then(setItems)
      .catch(() => setItems([]));
  }, []);
  useEffect(() => {
    if (active) load();
  }, [active, load]);

  if (!items || items.length === 0) return null;
  const open = items.filter((a) => a.status !== 'solved' && a.status !== 'submitted');
  const shown = [...open, ...items.filter((a) => !open.includes(a))].slice(0, 5);
  return (
    <section className="card form-card" aria-labelledby="homework-title">
      <h2 id="homework-title">
        {t.homework.title}
        {open.length > 0 && <span className="tag tag-accent"> {open.length}</span>}
      </h2>
      <HomeworkList items={shown} showClass onChange={load} />
    </section>
  );
}

/** My classes (or, for teachers, the classes I teach) and joining one by code. */
export function ClassesCard() {
  const t = useT();
  const me = useMe();
  const [list, setList] = useState<ClassSummary[] | null>(null);
  useEffect(() => {
    if (!me) return;
    let live = true;
    api
      .classes()
      .then((l) => live && setList(l))
      .catch(() => live && setList([]));
    return () => {
      live = false;
    };
  }, [me]);
  if (!me) return null;
  const teacher = isTeacher(me);
  return (
    <section className="card side-card" aria-labelledby="classes-title">
      <div className="side-card-head">
        <h2 id="classes-title">{t.classes.title}</h2>
        {teacher && (
          <a href="/classes" onClick={onLinkClick} className="btn btn-sm new-btn">
            <Icon name="plus" size={13} strokeWidth={2.6} />
            {t.classes.newClass}
          </a>
        )}
      </div>
      {list && list.length > 0 && (
        <ul className="project-list">
          {list.map((c) => (
            <li key={c.id}>
              <a href={`/classes/${c.id}`} onClick={onLinkClick}>
                <Icon name="users" size={14} />
                <span className="project-title">{c.name}</span>
                <span className="project-time">
                  {c.role === 'teacher' ? t.classes.students(c.studentCount) : c.teacherName}
                </span>
              </a>
            </li>
          ))}
        </ul>
      )}
      {!teacher && (
        <>
          <p>{t.classes.joinText}</p>
          <JoinClassForm />
        </>
      )}
    </section>
  );
}
